
import React from 'react';
import { PropertyField } from '../../molecules/PropertyField';
import { WorkflowNodeData } from '../../../types';
import { Plus, Trash2, Command, PlayCircle } from 'lucide-react';

interface UserTaskExtensionTabProps {
    data: WorkflowNodeData;
    onChange: (key: string, value: any) => void;
}

export const UserTaskExtensionTab: React.FC<UserTaskExtensionTabProps> = ({ data, onChange }) => {
    const type = data.userTaskType || 'generic';

    if (type === 'generic') {
        return (
            <div className="p-6 space-y-4">
                <div className="text-sm text-slate-500 italic p-4 border border-dashed rounded-lg bg-slate-50 dark:bg-slate-900">
                    Generic User Task selected. Configure PSS data below.
                </div>
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Advanced Config</h3>
                <PropertyField 
                    config={{ name: 'extensionConfig', label: 'Configuration (JSON)', type: 'object', rows: 4 }}
                    value={data.extensionConfig}
                    onChange={onChange}
                />
                <PropertyField 
                    config={{ name: 'extensionRelations', label: 'Relations (JSON)', type: 'object', rows: 4 }}
                    value={data.extensionRelations}
                    onChange={onChange}
                />
            </div>
        );
    }

    const operations = data.productionOperations || [];

    const handleAddOperation = () => {
        const newOps = [...operations, { key: `Op${operations.length + 1}`, value: '' }];
        onChange('productionOperations', newOps);
    };

    const handleRemoveOperation = (index: number) => {
        const newOps = operations.filter((_, i) => i !== index);
        onChange('productionOperations', newOps);
    };

    const handleOperationChange = (index: number, field: 'key' | 'value', val: string) => {
        const newOps = [...operations];
        newOps[index] = { ...newOps[index], [field]: val };
        onChange('productionOperations', newOps);
    };

    return (
        <div className="p-6 space-y-6">
            {type === 'produce' && (
                <div className="space-y-4 animate-in fade-in">
                    <h3 className="text-xs font-bold text-teal-600 uppercase tracking-wider border-b pb-2 mb-4">Production Config</h3>
                    
                    <PropertyField 
                        config={{ name: 'productionProcessKey', label: 'Process Key', type: 'text', placeholder: 'Badge.Produce' }}
                        value={data.productionProcessKey}
                        onChange={onChange}
                    />

                    <div className="space-y-3">
                        <div className="flex items-center justify-between">
                            <label className="text-xs font-bold text-slate-500 uppercase tracking-wider flex items-center gap-1">
                                <Command size={12}/> Operations
                            </label>
                            <button 
                                onClick={handleAddOperation}
                                className="text-xs flex items-center gap-1 text-teal-600 font-bold hover:bg-teal-50 dark:hover:bg-teal-900/30 px-2 py-1 rounded transition-colors"
                            >
                                <Plus size={12} /> Add
                            </button>
                        </div>

                        <div className="space-y-2">
                            {operations.map((op, index) => (
                                <div key={index} className="p-3 bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-lg flex flex-col gap-2 relative group">
                                    <button 
                                        onClick={() => handleRemoveOperation(index)}
                                        className="absolute top-2 right-2 p-1 text-slate-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors opacity-0 group-hover:opacity-100"
                                        title="Remove Operation"
                                    >
                                        <Trash2 size={12} />
                                    </button>

                                    <div className="grid grid-cols-[1fr] gap-2">
                                        <div>
                                            <label className="block text-[10px] text-slate-400 font-bold mb-1">Operation Key</label>
                                            <input 
                                                type="text" 
                                                value={op.key}
                                                onChange={(e) => handleOperationChange(index, 'key', e.target.value)}
                                                placeholder="e.g. CapturePhoto"
                                                className="w-full px-2 py-1.5 text-xs font-bold border rounded bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 focus:ring-1 focus:ring-teal-500 outline-none"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] text-slate-400 font-bold mb-1 flex items-center gap-1">
                                                <PlayCircle size={10}/> Script / Expression
                                            </label>
                                            <input // Using simple input here to save space, could be textarea
                                                type="text" 
                                                value={op.value}
                                                onChange={(e) => handleOperationChange(index, 'value', e.target.value)}
                                                placeholder="Script..."
                                                className="w-full px-2 py-1.5 text-xs font-mono border rounded bg-white dark:bg-slate-800 border-slate-300 dark:border-slate-700 focus:ring-1 focus:ring-teal-500 outline-none"
                                            />
                                        </div>
                                    </div>
                                </div>
                            ))}

                            {operations.length === 0 && (
                                <div className="text-center py-6 border-2 border-dashed border-slate-200 dark:border-slate-800 rounded-lg text-slate-400 text-xs italic">
                                    No operations defined.
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            )}

            {type === 'smartbridge' && (
                <div className="space-y-4 animate-in fade-in">
                    <h3 className="text-xs font-bold text-teal-600 uppercase tracking-wider border-b pb-2">SmartBridge Config</h3>
                    <PropertyField 
                        config={{ name: 'jobScript', label: 'Job Definition', type: 'object', placeholder: '{\n  "Action": "Print"\n}' }}
                        value={data.jobScript}
                        onChange={onChange}
                    />
                </div>
            )}

            {type === 'receipt' && (
                <div className="space-y-4 animate-in fade-in">
                    <h3 className="text-xs font-bold text-teal-600 uppercase tracking-wider border-b pb-2">Receipt Config</h3>
                    <div className="text-xs text-slate-500 mb-2">Configure using standard extension fields below.</div>
                </div>
            )}

            <div className="space-y-4 pt-4 border-t border-slate-200 dark:border-slate-800">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Engine Properties</h3>
                <div className="grid grid-cols-2 gap-4">
                    <PropertyField
                        config={{ name: 'userTaskSubject', label: 'Subject', type: 'text', placeholder: 'Leave empty to use node label', description: 'Task subject shown to the user.' }}
                        value={data.userTaskSubject}
                        onChange={onChange}
                    />
                    <PropertyField
                        config={{ name: 'userTaskObject', label: 'Object', type: 'text', placeholder: 'e.g. ${variables.objectId}', description: 'Business object reference.' }}
                        value={data.userTaskObject}
                        onChange={onChange}
                    />
                </div>
                <div className="grid grid-cols-3 gap-4">
                    <PropertyField
                        config={{ name: 'userTaskModifiers', label: 'Modifiers', type: 'text', placeholder: 'e.g. readonly' }}
                        value={data.userTaskModifiers}
                        onChange={onChange}
                    />
                    <PropertyField
                        config={{ name: 'userTaskStep', label: 'Step', type: 'number', defaultValue: 1, description: 'Workflow step number.' }}
                        value={data.userTaskStep}
                        onChange={onChange}
                    />
                    <PropertyField
                        config={{ name: 'userTaskRejectable', label: 'Rejectable', type: 'boolean', defaultValue: true, description: 'Allow task rejection.' }}
                        value={data.userTaskRejectable ?? true}
                        onChange={onChange}
                    />
                </div>
            </div>

            <div className="space-y-4 pt-4 border-t border-slate-200 dark:border-slate-800">
                <h3 className="text-xs font-bold text-slate-500 uppercase tracking-wider">Common Extensions</h3>
                <PropertyField
                    config={{ name: 'extensionConfig', label: 'Configuration (JSON)', type: 'object', rows: 4 }}
                    value={data.extensionConfig}
                    onChange={onChange}
                />
                <PropertyField
                    config={{ name: 'extensionRelations', label: 'Relations (JSON)', type: 'object', rows: 4 }}
                    value={data.extensionRelations}
                    onChange={onChange}
                />
            </div>
        </div>
    );
};
